import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  OnInit,
  Output,
  effect,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatTimepickerModule } from '@angular/material/timepicker';
import {
  RDS_DATE_FORMATS,
  RdsButtonComponent,
  RdsCheckboxComponent,
  RdsDateFormats,
  RdsDatepickerClearComponent,
  RdsDatepickerComponent,
  RdsDatepickerInputDirective,
  RdsDatepickerToggleComponent,
  RdsFormFieldComponent,
  RdsInputDirective,
  RdsSelectComponent,
  RdsSingleSelectOptionComponent,
} from '@rds/angular-components';
import { EMPTY, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';

import { RoomQueryParams, ViewportService, dateClass } from '@bookly/shared';

import { FiltersFormService } from './filters-form.service';

const CUSTOM_DATE_FORMATS: RdsDateFormats = {
  parse: {
    dateInput: null,
  },
  display: {
    dateInput: {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    },
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
  },
};

@Component({
  selector: 'bus-filter-form',
  imports: [
    RdsDatepickerClearComponent,
    RdsDatepickerToggleComponent,
    RdsDatepickerComponent,
    RdsDatepickerInputDirective,
    ReactiveFormsModule,
    MatInputModule,
    MatTimepickerModule,
    RdsFormFieldComponent,
    RdsInputDirective,
    RdsSelectComponent,
    RdsSingleSelectOptionComponent,
    RdsCheckboxComponent,
    RdsButtonComponent,
  ],
  providers: [
    {
      provide: RDS_DATE_FORMATS,
      useValue: CUSTOM_DATE_FORMATS,
    },
  ],
  templateUrl: './filter-form.component.html',
  styleUrl: './filter-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterFormComponent implements OnInit {
  @Output() filters = new EventEmitter<RoomQueryParams>();
  readonly #viewport = inject(ViewportService);
  protected readonly isMobileScreen = this.#viewport.isMobile;
  protected readonly dateClass = dateClass;

  readonly #filtersFormService = inject(FiltersFormService);
  readonly #destroyRef = inject(DestroyRef);

  readonly roomFiltersData = this.#filtersFormService.roomFiltersData;
  readonly buildings = this.#filtersFormService.buildings;
  readonly floors = this.#filtersFormService.floors;
  readonly filtersForm = this.#filtersFormService.filtersForm;
  readonly availableOnControl = this.#filtersFormService.availableOnControl;

  constructor() {
    effect(() => {
      if (this.roomFiltersData()) {
        this.#filtersFormService.populateRoomFilters();
      }
    });
  }

  ngOnInit() {
    this.#filtersFormService.siteIdValueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe();
    this.#filtersFormService.buildingIdValueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe();
    this.#filtersFormService.fromDateValueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe();
    this.#filtersFormService.availableOnControlValueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe();
    this.#viewport.isMobile$
      .pipe(
        switchMap(isMobile =>
          isMobile ? EMPTY : this.filtersForm.valueChanges
        ),
        distinctUntilChanged(),
        debounceTime(1000),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => this.applyFilters());
  }

  protected applyFilters() {
    this.filters.emit(this.#filtersFormService.getSelectedFormData());
  }

  protected resetForm() {
    this.#filtersFormService.resetForm();
  }
}
